import React, { useState } from 'react'
import Radom, { Deposit, DepositAccount } from '../../state/Radom'
import Table from '../../components/Table'
import { RADOM_COLORS } from '../../util/Constants'
import Tabs from '../../components/Tabs'
import { IconButton, PrimaryButton } from '../../components/Button'
import Copy from '../../icons/Copy'
import { errorToast, roundAssetAmount, shortAddress, successToast } from '../../util/Util'
import PaymentMethodDisplay from '../../components/PaymentMethodDisplay'
import { getMethod } from '../../util/Managed'
import { Chevron } from '../../icons/Chevron'
import TimeText from '../../components/TimeText'
import LinkExternal from '../../components/LinkExternal'
import { ExternalLinkText } from '../Events'
import { genSectionItems } from '../../util/SectionItems'
import { Section, SectionContent, SectionItem, SubTitle } from '../../components/Shared'
import CreateDepositAddresCustomerModal from './CreateDepositAddresCustomerModal'

interface CustomerDepositAccountsProps {
  depositAccounts?: DepositAccount[]
  isLoading: boolean
  customerId: string
}

const CustomerDepositAccounts = ({
  depositAccounts,
  isLoading,
  customerId
}: CustomerDepositAccountsProps): JSX.Element => {
  const [selectedTab, setSelectedTab] = useState<string>('Details')
  const [selectedAccount, setSelectedAccount] = useState<DepositAccount | null>(null)
  const [showDetailsPanel, setShowDetailsPanel] = useState<boolean>(false)
  const [deposits, setDeposits] = useState<Deposit[]>([])
  const [isLoadingDeposits, setIsLoadingDeposits] = useState(false)
  const [isCreateAccountModalOpen, setIsCreateAccountModalOpen] = useState(false)

  const handleModalOpen = (): void => {
    setIsCreateAccountModalOpen(true)
  }
  const handleModalClose = (): void => {
    setIsCreateAccountModalOpen(false)
  }

  const fetchDeposits = async (id: string): Promise<void> => {
    setIsLoadingDeposits(true)
    setDeposits([])

    Radom.listDepositsByAccountId(id)
      .then(res => {
        setDeposits(res)
      })
      .finally(() => setIsLoadingDeposits(false))
  }

  if (!depositAccounts) {
    return <div>Loading...</div>
  }

  if (depositAccounts.length === 0) {
    return (
      <>
        <div style={{
          padding: 40,
          textAlign: 'center',
          fontSize: 14,
          color: RADOM_COLORS.GRAY4
        }}>
        No deposit accounts yet.{' '}
          <a
            href="#"
            style={{
              color: '#4C7BF4',
              textDecoration: 'none',
              fontWeight: 500,
              cursor: 'pointer'
            }}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              handleModalOpen()
            }}
          >
          Create one
          </a>
        </div>

        <CreateDepositAddresCustomerModal
          customerId={customerId}
          title='Create deposit account'
          visible={isCreateAccountModalOpen}
          onClose={handleModalClose}
          onAccountCreated={() => {
            handleModalClose()
            // TODO FETCH NEW LIST
            // fetchDepositAddresses()
          }} />
      </>
    )
  }

  return (
    <>
      <PrimaryButton style={{ width: 180, alignSelf: 'end' }} onClick={handleModalOpen}>Create address</PrimaryButton>
      <Section>
        <SubTitle style={{ fontSize: 18 }}>
                  Crypto Deposit Accounts
        </SubTitle>
        {
          depositAccounts.map((account, idx) => (
            <Section
              key={account.id}
              style={{
                backgroundColor: RADOM_COLORS.WHITE,
                cursor: 'pointer'
              }}
              onClick={() => {
                setSelectedTab('Details')
                setSelectedAccount(account)
                setShowDetailsPanel(true)
                if (deposits.length === 0 ||
                  deposits[0].depositAccountId !== account.id) {
                  fetchDeposits(account.id)
                }
              }}
            >
              <SectionContent numColumns={3}>
                {genSectionItems([
                  { heading: 'Account ID', content: account.name },
                  { heading: 'Deposit Address', content: account.accountDetails.Crypto?.address },
                  <SectionItem key={idx}>
                    <SubTitle>Supported Assets</SubTitle>
                    {account.accountDetails.Crypto && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {account.accountDetails.Crypto.depositMethods.map(m => (
                          <PaymentMethodDisplay
                            key={m.network + (m.token ?? '')}
                            method={{ network: m.network, token: m.token }}
                            excludeName
                            disableTooltip={false}
                          />
                        ))}
                      </div>
                    )}
                  </SectionItem>
                ], 3)}
              </SectionContent>
            </Section>
          ))
        }
      </Section>
      {showDetailsPanel && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(7, 7, 40, 0.75)',
            zIndex: 99
          }}
          onClick={() => setShowDetailsPanel(false)}
        />
      )}

      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: 'auto',
          height: '100%',
          background: 'white',
          boxSizing: 'border-box',
          boxShadow: '-2px 0 5px rgba(0,0,0,0.3)',
          transform: showDetailsPanel ? 'none' : 'translateX(150%)',
          transition: 'transform 0.3s ease',
          zIndex: 100,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          overflow: 'hidden'
        }}
      >
        <div
          style={{
            fontSize: 14,
            width: 550,
            height: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: 20,
            paddingTop: 60,
            gap: 16,
            overflowY: 'auto'
          }}
        >
          <div style={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 18 }}>{selectedAccount?.name}</span>
          </div>

          <div
            style={{
              marginTop: 24,
              marginBottom: 24
            }}
          >
            <Tabs
              selectedTab={selectedTab}
              tabs={['Details', 'Deposits']}
              onTabChange={(tab) => { setSelectedTab(tab) }} />
          </div>

          {selectedTab === 'Details'
            ? <>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                <span style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>Deposit Address ID</span>
                <div style={{
                  display: 'flex',
                  gap: 4
                }}>
                  <span
                    style={{
                      color: RADOM_COLORS.RADOM_BLUE,
                      cursor: 'pointer'
                    }}
                  >
                    {selectedAccount?.id}
                  </span>
                  <IconButton style={{ padding: 0 }}>
                    <Copy style={{ width: 16, stroke: RADOM_COLORS.RADOM_BLUE }} onClick={(e) => {
                      e.stopPropagation()
                      const textToCopy = selectedAccount?.id ?? ''
                      navigator.clipboard
                        .writeText(textToCopy)
                        .then(() => {
                          successToast('Copied Deposit Account ID')
                        })
                        .catch((err) => {
                          console.error('copy failed', err)
                          errorToast('Failed to copy Deposit Account ID')
                        })
                    }} />
                  </IconButton>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                <span style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>Deposit Address</span>
                <div style={{
                  display: 'flex',
                  gap: 4
                }}>
                  <span
                    style={{
                      color: RADOM_COLORS.RADOM_BLUE,
                      cursor: 'pointer'
                    }}
                  >
                    {selectedAccount?.accountDetails.Crypto?.address}
                  </span>
                  <IconButton style={{ padding: 0 }}>
                    <Copy style={{ width: 16, stroke: RADOM_COLORS.RADOM_BLUE }} onClick={(e) => {
                      e.stopPropagation()
                      const textToCopy = selectedAccount?.accountDetails.Crypto?.address ?? ''
                      navigator.clipboard
                        .writeText(textToCopy)
                        .then(() => {
                          successToast('Copied Deposit Address')
                        })
                        .catch((err) => {
                          console.error('copy failed', err)
                          errorToast('Failed to copy Deposit Address')
                        })
                    }} />
                  </IconButton>
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6
                }}
              >
                <span style={{ color: RADOM_COLORS.NEW_GRAY_1 }}>Supported Assets</span>
                {selectedAccount?.accountDetails.Crypto && <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {
                    selectedAccount.accountDetails.Crypto?.depositMethods.map(m =>
                      <PaymentMethodDisplay
                        key={m.network + (m.token ?? '')}
                        method={{ network: m.network, token: m.token }}
                        excludeName
                        disableTooltip={false} />
                    )
                  }
                </div>}
              </div>
            </>
            : <>
              <Table
                borderless
                disableOnClickState
                headers={['Deposit ID', 'Summary', 'Date'].map(h => <span key={h}>{h}</span>)}
                isLoading={isLoadingDeposits}
                items={deposits.map(d => {
                  let summary: {
                    in: {
                      img: string
                      amount: number | string
                      ticker: string
                    }
                    out?: {
                      img: string
                      amount: number | string
                      ticker: string
                    }
                  }

                  if (d.depositMethod.Crypto) {
                    const method = getMethod(d.depositMethod.Crypto.network, d.depositMethod.Crypto.token)
                    summary = {
                      in: {
                        img: method.logo,
                        amount: d.netAmount,
                        ticker: method.ticker
                      }
                    }
                  } else {
                    const outMethod = getMethod(d.depositMethod.Fiat?.onrampNetwork || '', d.depositMethod.Fiat?.onrampToken)
                    const inMethod = getMethod('Fiat', d.depositMethod.Fiat?.fiatCurrency)

                    summary = {
                      in: {
                        img: inMethod.logo,
                        amount: d.fiatGrossAmount || '',
                        ticker: inMethod.ticker
                      },
                      out: {
                        img: outMethod.logo,
                        amount: d.netAmount,
                        ticker: outMethod.ticker
                      }
                    }
                  }
                  return [
                    <div key={'1'}>
                      <ExternalLinkText
                        onClick={e => e.stopPropagation()}
                        target="_blank"
                        href={`/deposit/${d.id}?source=transactions`}>
                        {shortAddress(d.id, 3)}
                        <LinkExternal stroke={RADOM_COLORS.BLACK} width={14} />
                      </ExternalLinkText>
                    </div>,
                    <div key={'2'} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <img src={summary.in.img} style={{ width: 20, height: 20 }} />
                      <span>
                        {roundAssetAmount(summary.in.ticker, Number(summary.in.amount))}
                      </span>
                      <span>{summary.in.ticker}</span>
                      {
                        summary.out && <>
                          <Chevron style={{ opacity: 0.65, transform: 'rotate(-90deg)' }} />
                          <img src={summary.out.img} style={{ width: 20, height: 20 }} />
                          <span>
                            {roundAssetAmount(summary.out.ticker, Number(summary.out.amount))}
                          </span>
                          <span>{summary.out.ticker}</span>
                        </>
                      }
                    </div>,
                    <div key={'3'}>
                      <TimeText date={new Date(d.depositedAt)} />
                    </div>
                  ]
                })}
                emptyPlaceholderText='No deposits yet'
              />
            </>}
        </div>
      </div>
      {

        <CreateDepositAddresCustomerModal
          customerId={customerId}
          title='Create deposit account'
          visible={isCreateAccountModalOpen}
          onClose={handleModalClose}
          onAccountCreated={() => {
            handleModalClose()
            // TODO FETCH NEW LIST
            // fetchDepositAddresses()
          }} />
      }
    </>
  )
}

export default CustomerDepositAccounts
